import { LitElement, css, html, nothing, type PropertyValues, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { live } from 'lit/directives/live.js';

import { nsFormFieldStyles, nsSharedStyles } from '../ns-styles/index.js';
import { renderFormField } from '../helpers/form-field.js';
import {
  COUNTRY_OPTIONS,
  EMPTY_BILLING_DRAFT,
  billingProblemsFromMessage,
  parseBillingDraft,
  type BillingDetails,
  type BillingDraft,
  type BillingField,
  type BillingProblem,
} from '../helpers/billing-details.js';

export interface BillingChangeDetail {
  draft: BillingDraft;
  // The normalised details, or null while a field has a problem
  billing: BillingDetails | null;
}

interface FieldSpec {
  field: BillingField;
  label: string;
  autocomplete: string;
  type?: 'email' | 'tel' | 'text';
  required: boolean;
}

const FIELDS: Record<Exclude<BillingField, 'country'>, FieldSpec> = {
  email: { field: 'email', label: 'Email', autocomplete: 'billing email', type: 'email', required: true },
  phone: { field: 'phone', label: 'Phone (optional)', autocomplete: 'billing tel', type: 'tel', required: false },
  address1: { field: 'address1', label: 'Address line 1', autocomplete: 'billing address-line1', required: true },
  address2: {
    field: 'address2',
    label: 'Address line 2 (optional)',
    autocomplete: 'billing address-line2',
    required: false,
  },
  city: { field: 'city', label: 'Town or city', autocomplete: 'billing address-level2', required: true },
  postcode: { field: 'postcode', label: 'Postcode', autocomplete: 'billing postal-code', required: true },
};

const FORM_ORDER: BillingField[] = ['email', 'phone', 'address1', 'address2', 'city', 'postcode', 'country'];

/**
 * A company's billing details (#41 part 5), for the onboarding payment card and the automatic payments set-up card: Email,
 * Phone (optional), Address lines 1 and 2 (2 optional), Town or city, Postcode and Country (an ISO-2 select, the UK
 * first). Set `.prefill` to the starting text (`billingPrefillFromCompany`); every value stays editable. With no
 * company email the Email hint asks for it (no fallback to the payer's).
 *
 * Read `.value` for the typed text and `.billing` for the normalised details (null while a field has a problem). Each
 * edit fires `billing-change` with `{draft, billing}`. A field shows its problem once it has been left or the card calls
 * `reportValidity()`, which shows every problem and focuses the first; `checkValidity()` only answers.
 * `showServerProblems(message)` puts a refusal's wording beside the fields it names.
 */
@customElement('ns-billing-details')
export class NSBillingDetails extends LitElement {
  // The legend above the fields
  @property({ type: String })
  legend = 'Billing details';

  @property({ attribute: false })
  prefill: Partial<BillingDraft> | null = null;

  @property({ type: Boolean, reflect: true })
  disabled = false;

  @state()
  private __draft: BillingDraft = { ...EMPTY_BILLING_DRAFT };

  // Problems shown beside their fields
  @state()
  private __shown: Partial<Record<BillingField, string>> = {};

  // Fields the person has left (a problem shows from then on)
  private __touched = new Set<BillingField>();

  // Whether the card asked for every problem to show (reportValidity)
  private __reported = false;

  // Whether the prefill had no email: the hint then asks for the company's
  @state()
  private __noCompanyEmail = true;

  static styles = [
    nsSharedStyles,
    nsFormFieldStyles,
    css`
      :host {
        display: block;
      }

      :host([hidden]) {
        display: none;
      }

      fieldset {
        display: grid;
        gap: var(--wa-space-m);
        min-width: 0;
        margin: 0;
        padding: 0;
        border: 0;
      }

      legend {
        margin: 0 0 var(--wa-space-m);
        padding: 0;
        font-size: var(--wa-font-size-l);
        font-weight: 500;
      }

      .intro::slotted(*) {
        margin: 0;
        font-weight: 300;
      }

      .field input,
      .field select {
        width: 100%;
        max-width: 24rem;
      }

      .field-row {
        flex-wrap: wrap;
      }

      .field-row .field {
        min-width: 10rem;
      }

      input:focus-visible,
      select:focus-visible {
        outline: 2px solid var(--wa-color-focus);
        outline-offset: 2px;
      }
    `,
  ];

  willUpdate(changed: PropertyValues<this>) {
    // A new prefill starts the form again
    if (changed.has('prefill')) {
      this.__draft = { ...EMPTY_BILLING_DRAFT, ...(this.prefill ?? {}) };
      this.__noCompanyEmail = !this.__draft.email.trim();
      this.__touched.clear();
      this.__reported = false;
      this.__shown = {};
    }
  }

  /** The typed text. */
  get value(): BillingDraft {
    return { ...this.__draft };
  }

  /** The normalised billing details, or null while a field has a problem. */
  get billing(): BillingDetails | null {
    const parsed = parseBillingDraft(this.__draft);
    return parsed.ok ? parsed.billing : null;
  }

  /** The problems with the typed text, in the form's order. */
  get problems(): BillingProblem[] {
    const parsed = parseBillingDraft(this.__draft);
    return parsed.ok ? [] : parsed.problems;
  }

  /** Whether the details can be sent, without showing anything. */
  checkValidity(): boolean {
    return this.problems.length === 0;
  }

  /** Shows every problem beside its field and focuses the first; true when there are none. */
  reportValidity(): boolean {
    this.__reported = true;
    const problems = this.problems;
    this.__shown = Object.fromEntries(problems.map((p) => [p.field, p.reason]));
    if (problems.length > 0) void this.__focus(problems[0].field);
    return problems.length === 0;
  }

  /**
   * Shows a server refusal's wording beside the fields it names and focuses the first; false when it names none (the card
   * then shows the whole message).
   */
  showServerProblems(message: string): boolean {
    const problems = billingProblemsFromMessage(message);
    if (problems.length === 0) return false;
    this.__shown = Object.fromEntries(problems.map((p) => [p.field, p.reason]));
    void this.__focus(problems[0].field);
    return true;
  }

  private async __focus(field: BillingField) {
    await this.updateComplete;
    (this.shadowRoot?.getElementById(`billing-${field}`) as HTMLElement | null)?.focus();
  }

  // The problems to show: every one once reported, otherwise those of the fields left so far
  private __visibleProblems(): Partial<Record<BillingField, string>> {
    const all = Object.fromEntries(this.problems.map((p) => [p.field, p.reason])) as Partial<
      Record<BillingField, string>
    >;
    if (this.__reported) return all;
    const shown: Partial<Record<BillingField, string>> = {};
    for (const field of FORM_ORDER) if (this.__touched.has(field) && all[field]) shown[field] = all[field];
    return shown;
  }

  private __set(field: BillingField, value: string) {
    this.__draft = { ...this.__draft, [field]: value };
    // A shown problem is cleared or updated as the field changes; a new one waits until the field is left
    const visible = this.__visibleProblems();
    const shown = { ...this.__shown };
    if (visible[field]) shown[field] = visible[field];
    else delete shown[field];
    this.__shown = shown;
    this.dispatchEvent(
      new CustomEvent<BillingChangeDetail>('billing-change', {
        detail: { draft: this.value, billing: this.billing },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private __leave(field: BillingField) {
    this.__touched.add(field);
    const reason = this.__visibleProblems()[field];
    const shown = { ...this.__shown };
    if (reason) shown[field] = reason;
    else delete shown[field];
    this.__shown = shown;
  }

  private __describedBy(id: string, hint: boolean, error: boolean) {
    const ids = [hint ? `${id}-hint` : '', error ? `${id}-error` : ''].filter(Boolean).join(' ');
    return ids || nothing;
  }

  private __renderInput(spec: FieldSpec, hint?: string): TemplateResult {
    const id = `billing-${spec.field}`;
    const error = this.__shown[spec.field];
    return renderFormField({
      id,
      label: spec.label,
      hint: hint
        ? html`
            <span id="${id}-hint">${hint}</span>
          `
        : undefined,
      error,
      control: html`
        <input
          id=${id}
          type=${spec.type ?? 'text'}
          autocomplete=${spec.autocomplete}
          ?required=${spec.required}
          aria-required=${spec.required ? 'true' : nothing}
          aria-invalid=${error ? 'true' : 'false'}
          aria-describedby=${this.__describedBy(id, !!hint, !!error)}
          ?disabled=${this.disabled}
          .value=${live(this.__draft[spec.field])}
          @input=${(e: Event) => this.__set(spec.field, (e.target as HTMLInputElement).value)}
          @blur=${() => this.__leave(spec.field)}
        />
      `,
    });
  }

  private __renderCountry(): TemplateResult {
    const id = 'billing-country';
    const error = this.__shown.country;
    const value = this.__draft.country;
    return renderFormField({
      id,
      label: 'Country',
      error,
      control: html`
        <select
          id=${id}
          autocomplete="billing country"
          required
          aria-required="true"
          aria-invalid=${error ? 'true' : 'false'}
          aria-describedby=${this.__describedBy(id, false, !!error)}
          ?disabled=${this.disabled}
          .value=${live(value)}
          @change=${(e: Event) => {
            this.__set('country', (e.target as HTMLSelectElement).value);
            this.__leave('country');
          }}
          @blur=${() => this.__leave('country')}
        >
          <option value="" ?selected=${value === ''}>Choose a country</option>
          ${COUNTRY_OPTIONS.map(
            ({ code, name }) => html`
              <option value=${code} ?selected=${value === code}>${name}</option>
            `,
          )}
        </select>
      `,
    });
  }

  render() {
    const emailHint = this.__noCompanyEmail
      ? "Your company has no contact email on its profile. Enter the email your company's invoices should go to."
      : "The email your company's invoices go to.";
    return html`
      <fieldset>
        <legend>${this.legend}</legend>
        <slot class="intro"></slot>
        ${this.__renderInput(FIELDS.email, emailHint)} ${this.__renderInput(FIELDS.phone)}
        ${this.__renderInput(FIELDS.address1)} ${this.__renderInput(FIELDS.address2)}
        <div class="field-row">${this.__renderInput(FIELDS.city)} ${this.__renderInput(FIELDS.postcode)}</div>
        ${this.__renderCountry()}
      </fieldset>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ns-billing-details': NSBillingDetails;
  }
}
